/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-05 01:25:47
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Page, TextStyle, Banner, Stack } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { connect } from 'react-redux';

import { routes } from 'src/routes';
import history from 'src/routes/history';
import { getShop } from 'src/helpers';

import * as OauthAction from 'actions/oauth-actions';
import * as AppPermissionActions from 'actions/app-permission-actions';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';

type Props = WithI18nProps;

class UpdateAppPermissionSuccess extends Component<Props, State> {
  state = {
    redirect: {}
  };

  componentDidMount() {
    let redirectURI = '/';
    try {
      redirectURI = localStorage.getItem(`${getShop()}-permissionApproval-redirectURI`);
    } catch (error) {}

    const redirect = this.getRedirectPage(redirectURI || '/');

    // Save redirect info
    this.setState({ redirect });

    // Update app scopes
    if (redirect) {
      this.props.updateScopes();
    }
  }

  componentWillReceiveProps(nextProps) {
    const { success } = this.props;
    const { success: successNext, accessScopes } = nextProps;

    // Update successfully, remove localstorage
    if (successNext === true && success === false) {
      // Update tenant app scopes in state
      this.props.updateAppScopes(accessScopes);
      // Tracking event
      const { redirect } = this.state;
      if (redirect?.uri === '/recommendation-widgets') {
        mixpanelV3Helper.trackEventData('Grant Access for Recommendation', {
          feature: 'Recommendation'
        });
      }
    }
  }

  render() {
    const { redirect } = this.state;
    const { i18n, success } = this.props;

    const formattedRedirect = _.isEmpty(redirect)
      ? redirect
      : {
          ...redirect,
          title: i18n.translate(redirect.title),
          content: i18n.translate(redirect.content),
          bannerTitle: i18n.translate(redirect.bannerTitle),
          actionPage: i18n.translate(redirect.actionPage)
        };
    return (
      <div className="BC-Page BC-Page_Success">
        <Page
          title={formattedRedirect.title}
          breadcrumbs={[
            {
              content: formattedRedirect.title,
              onAction: formattedRedirect.onAction
            }
          ]}>
          <br />
          <Banner
            title={formattedRedirect.bannerTitle}
            status="success"
            action={{
              content: i18n.translate('AdminHTML.AppPermission.Success.success_ButtonChildren', {
                nt_page_title: formattedRedirect.actionPage
              }),
              onAction: formattedRedirect.onAction,
              loading: !success
            }}>
            <Stack vertical={true}>
              <TextStyle>{formattedRedirect.content}</TextStyle>
            </Stack>
          </Banner>
        </Page>
      </div>
    );
  }

  getRedirectPage = redirectURI => {
    const redirects = [
      {
        uri: '/search/instant-search-display',
        title: 'AdminHTML.AppPermission.Success.success_Redirect1_Title',
        content: 'AdminHTML.AppPermission.Success.success_Redirect1_Content',
        bannerTitle: 'AdminHTML.AppPermission.Success.success_BannerTitle',
        actionPage: 'AdminHTML.AppPermission.Success.success_Redirect1_Title',
        onAction: () => {
          history.push(routes.instantSearchDisplay.view());
        }
      },
      {
        uri: '/translation',
        title: 'AdminHTML.AppPermission.Success.success_Redirect2_Title',
        content: 'AdminHTML.AppPermission.Success.success_Redirect2_Content',
        bannerTitle: 'AdminHTML.AppPermission.Success.success_BannerTitle',
        actionPage: 'AdminHTML.AppPermission.Success.success_Redirect2_Title',
        onAction: () => {
          history.push(routes.translation.view());
        }
      },
      {
        uri: '/analytics',
        title: 'AdminHTML.AppPermission.Success.success_Redirect3_Title',
        content: 'AdminHTML.AppPermission.Success.success_Redirect_Content_SuccessPermissionUpdate',
        bannerTitle: 'AdminHTML.AppPermission.Success.success_BannerTitle',
        actionPage: 'AdminHTML.AppPermission.Success.success_Redirect3_Title',
        onAction: () => {
          history.push({ pathname: routes.analytics.view(), state: { selectedTab: 2 } });
        }
      },
      {
        uri: '/',
        title: 'AdminHTML.AppPermission.Success.success_Redirect4_Title',
        content: 'AdminHTML.AppPermission.Success.success_Redirect_Content_SuccessPermissionUpdate',
        bannerTitle: 'AdminHTML.AppPermission.Success.success_BannerTitle',
        actionPage: 'AdminHTML.AppPermission.Success.success_Redirect4_Title',
        onAction: () => {
          history.push(routes.dashboard.view());
        }
      },
      {
        uri: '/recommendation-widgets',
        title: 'AdminHTML.AppPermission.Success.success_Redirect5_Title',
        content:
          'AdminHTML.AppPermission.Success.success_Redirect_Content_RecommendationPermissionGranted',
        bannerTitle: 'AdminHTML.AppPermission.Success.success_BannerTitle2',
        actionPage: 'AdminHTML.AppPermission.Success.success_Action_Content_RecommendationWidgets',
        onAction: () => {
          history.push(routes.recommendationWidgets.list());
        }
      },
      {
        uri: '/manual-recommendation',
        title: 'AdminHTML.AppPermission.Success.success_Redirect5_Title',
        content:
          'AdminHTML.AppPermission.Success.success_Redirect_Content_RecommendationPermissionGranted',
        bannerTitle: 'AdminHTML.AppPermission.Success.success_BannerTitle2',
        actionPage: 'AdminHTML.AppPermission.Success.success_Action_Content_ManualRecommendation',
        onAction: () => {
          history.push(routes.manualRecommendation.list());
        }
      }
    ];

    return _.find(redirects, { uri: redirectURI });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateScopes: () => dispatch(AppPermissionActions.updateScopes(ownProps.i18n)),
  updateAppScopes: accessScopes => dispatch(OauthAction.updateAppScopes(accessScopes))
});

const mapStateToProps = (state, props) => ({
  success: _.get(state.appPermission, 'success', false),
  accessScopes: _.get(state.appPermission, 'accessScopes', {})
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(UpdateAppPermissionSuccess));
